<template>
  <v-app id="inspire">
    <v-system-bar v-if="options.showSystemBar" app>
      <slot name="systembar"></slot>
    </v-system-bar>
    <!--Display two-factor banner if the user has not set up two-factor authentication-->
    <two-factor-banner
      v-if="
        !user?.isTwoFactorAuthenticationEnabled &&
        !user?.twoFactorAuthenticationSecret
      "
    />
    <!-- Component used to determine if user is idle -->
    <AuthIdle />
    <v-app-bar
      :color="options.appBarColor"
      class="shadow"
      elevate-on-scroll
      app
      clipped-right
      height="72"
    >
      <v-app-bar-nav-icon
        v-if="$vuetify.breakpoint.mobile && options.showMenuToggle"
        @click="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <slot name="title">
        <v-toolbar-title class="font-weight-bold d-flex">
          App Center
        </v-toolbar-title>
        <v-icon small style="margin-top: -20px">mdi-apps</v-icon>
      </slot>

      <v-spacer></v-spacer>

      <v-responsive
        v-if="!$vuetify.breakpoint.mobile && options.showSearchBar"
        max-width="156"
      >
        <v-text-field
          dense
          flat
          hide-details
          rounded
          placeholder="Search..."
          solo-inverted
        ></v-text-field>
      </v-responsive>

      <slot name="toolbar"></slot>

      <v-menu
        v-if="user"
        rounded="xs"
        :nudge-bottom="10"
        content-class="shadow-md"
        offset-y
        bottom
        left
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-if="options.showUserOptions"
            depressed
            v-bind="attrs"
            v-on="on"
            text
            style="border: 1px solid #eee"
          >
            <v-avatar size="26" class="mr-2">
              <v-img :src="getUserAvatar">
                <template v-slot:placeholder>
                  <v-icon class="mr-2">mdi-account-circle-outline</v-icon>
                </template>
              </v-img>
            </v-avatar>
            <span class="text-capitalize font-weight-medium" v-if="!$vuetify.breakpoint.mobile">{{
              userFullName
            }}</span>
            <v-icon class="ml-3">mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-list dense>
          <v-list-item
            three-line
            class="text-center align-center justify-center"
          >
            <div class="d-flex flex-column">
              <v-avatar
                size="70"
                class="grey lighten-3 text-center align-self-center"
              >
                <v-img v-if="getUserAvatar" :src="getUserAvatar"></v-img>
                <v-icon v-else size="40">mdi-account-outline</v-icon>
              </v-avatar>
              <span
                class="text-capitalize mt-2 mb-1 font-weight-medium grey--text text--darken-3"
                >{{ userFullName }}</span
              >

              <span class="text-capitalize text-caption font-weight-light"
                ><b>{{ userRole }}</b></span
              >
              <span class="caption font-weight-light">{{ user.email }}</span>
            </div>
          </v-list-item>

          <v-divider class="mt-3" />

          <v-list-item
            class="small-list-item"
            @click="$refs.accountSettingsDialog?.open()"
          >
            <v-list-item-title
              class="grey--text text--darken-3 font-weight-light"
              >Manage your account settings</v-list-item-title
            >
            <v-list-item-action>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <v-list-item class="small-list-item" to="/app/audit-reports/user">
            <v-list-item-title
              class="grey--text text--darken-3 font-weight-light"
              >View account stats</v-list-item-title
            >
            <v-list-item-action>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-list-item
            v-if="
              user?.isTwoFactorAuthenticationEnabled &&
              user?.twoFactorAuthenticationSecret
            "
            class="small-list-item"
            @click="showEnableDisable2FA = true"
          >
            <v-list-item-title
              class="grey--text text--darken-3 font-weight-light"
              >Disable 2FA</v-list-item-title
            >
            <v-list-item-action>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-list-item
            v-if="
              !user?.isTwoFactorAuthenticationEnabled &&
              user?.twoFactorAuthenticationSecret
            "
            class="small-list-item"
            @click="showEnableDisable2FA = true"
          >
            <v-list-item-title
              class="grey--text text--darken-3 font-weight-light"
              >Enable 2FA</v-list-item-title
            >
            <v-list-item-action>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
          <v-list-item
            v-if="user?.twoFactorAuthenticationSecret"
            class="small-list-item"
            @click="showGenerateBackupCodes = true"
          >
            <v-list-item-title
              class="grey--text text--darken-3 font-weight-light"
              >Generate New 2FA Backup Codes</v-list-item-title
            >
            <v-list-item-action>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>

          <v-divider class="mb-1" />

          <v-list-item class="small-list-item" @click="logOut()">
            <v-list-item-title
              class="grey--text text--darken-3 font-weight-light"
              >Sign Out</v-list-item-title
            >
            <v-list-item-action>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn @click="toggleRightDrawer" v-if="showRightDrawer" icon>
        <v-icon>mdi-tune-vertical</v-icon>
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer
      app
      hide-overlay
      v-model="drawer"
      :mini-variant.sync="mini"
      width="370"
      permanent
      expand-on-hover
    >
      <template #prepend>
        <slot name="navheader">
          <v-sheet color="darken-1">
            <v-toolbar style="padding-left: 40px" flat>
              <v-list-item three-line dense>
                <img
                  class="mt-1"
                  style="height: 36px"
                  src="@/assets/logos/app.png"
                  alt=""
                />
                <v-spacer />
                <span class="font-weight-bold">
                </span>
              </v-list-item>
            </v-toolbar>
          </v-sheet>
        </slot>
      </template>

      <v-navigation-drawer
        :permanent="$vuetify.breakpoint.mobile && drawer"
        hide-overlay
        v-model="drawer"
        absolute
        color="primary darken-2"
        mini-variant
      >
        <v-tooltip right>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="d-block mx-auto mt-3 mb-1"
              icon
              large
              to="/app"
            >
              <v-icon class="mt-2" :color="options.miniDrawerIconsColor"
                >mdi-apps</v-icon
              >
            </v-btn>
          </template>
          <span>Apps</span>
        </v-tooltip>

        <v-divider class="mx-3 my-4"></v-divider>

        <template v-if="mini">
          <template v-for="item in links">
            <v-btn
              v-if="userHasPrivilege(item?.requiredPrivileges, item)"
              :key="item.heading || item.title"
              icon
              large
              class="d-block text-center mx-auto mb-1"
              @click="mini = !mini"
            >
              <v-icon color="white">{{ item.icon }}</v-icon>
            </v-btn>
          </template>
          <v-tooltip v-if="showActionItems && userHasPrivilege([])" right>
            <template #activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                large
                class="d-block text-center mx-auto mb-1"
                @click="$refs.actionItemsIntro.open()"
              >
                <v-icon color="white">mdi-clipboard-text-clock-outline</v-icon>
              </v-btn>
            </template>
            <span>Action Items Task</span>
          </v-tooltip>
        </template>

        <template v-if="mini" #append> </template>
      </v-navigation-drawer>

      <v-list class="pl-14 pt-6" dense>
        <template v-for="item in links">
          <v-list-group
            v-if="
              item.heading &&
              !itemHasChildren(item) &&
              userHasPrivilege(item?.requiredPrivileges, item)
            "
            :key="item.heading"
            v-model="item.active"
            :prepend-icon="item.icon"
            no-action
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title class="grey--text text--darken-3">
                  <span class="mr-2">
                    {{ item.heading }}
                    <v-badge
                      v-if="item.badgeText"
                      inline
                      color="blue lighten-2"
                    >
                      <template v-slot:badge>
                        {{ item.badgeText }}
                      </template>
                    </v-badge>
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </template>

            <template v-for="child in item.items">
              <v-list-item
                :to="child.to"
                :href="child.href"
                class="pl-10"
                :key="child.title"
                link
              >
                <v-list-item-icon v-if="child.icon">
                  <v-icon>
                    {{ child.icon }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title
                    class="grey--text text--darken-2"
                    v-text="child.title"
                  ></v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="child.badgeText">
                  <v-chip label :color="child.badgeColor" x-small>{{
                    child.badgeText
                  }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list-group>

          <v-list-group
            v-else-if="
              item.heading &&
              itemHasChildren(item) &&
              userHasPrivilege(item?.requiredPrivileges, item)
            "
            :key="item.heading"
            :prepend-icon="item.icon"
          >
            <template v-slot:activator>
              <v-list-item-title class="grey--text text--darken-3"
                >{{ item.heading }}
              </v-list-item-title>
            </template>

            <template v-for="child in item.items">
              <v-list-group
                class="pl-4"
                v-if="child.heading"
                :key="`childItem${child.heading}`"
                no-action
                sub-group
              >
                <template v-slot:activator>
                  <v-list-item-title class="grey--text text--darken-3">{{
                    child.heading
                  }}</v-list-item-title>
                </template>
                <v-list-item
                  :to="k.to"
                  :href="k.href"
                  v-for="k in child.items"
                  class="pl-10"
                  :key="k.title"
                  link
                >
                  <v-list-item-icon>
                    <v-icon>
                      {{ k.icon }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title
                      class="grey--text text--darken-2"
                      v-text="k.title"
                    ></v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>

              <v-list-item
                v-else
                :to="child.to"
                :href="child.href"
                class="pl-10"
                :key="child.title"
                link
              >
                <v-list-item-icon>
                  <v-icon>
                    {{ child.icon }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title
                    class="grey--text text--darken-2"
                    v-text="child.title"
                  ></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list-group>

          <v-list-item
            active-class="primary--text"
            v-else-if="userHasPrivilege(item?.requiredPrivileges, item)"
            :key="item.title"
            :to="item.to"
            link
          >
            <v-list-item-icon>
              <v-icon>
                {{ item.icon }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>

        <v-list-item v-if="showActionItems && userHasPrivilege([])" link @click="$refs.actionItemsIntro.open()">
          <v-list-item-icon>
            <v-icon>mdi-clipboard-text-clock-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Action Items Task</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <template #append>
        <div class="pa-2 text-center"> 
          <a
            href="https://doc.clickup.com/14173659/p/h/dghev-10137/434ae73e1c71375"
            target="_blank"
            class="text-decoration-none text-caption d-inline-block text--darken-1"
            style="margin: 0 auto;" 
          >
            Privacy Notice
          </a>
        </div>
      </template>
    </v-navigation-drawer>

    <AccountSettingsDialog ref="accountSettingsDialog" />
    <ActionItemsIntro v-if="showActionItems" ref="actionItemsIntro" :action-items="actionItems" />

    <!-- Modal used to toggle 2FA -->
    <enable-disable-two-factor v-model="showEnableDisable2FA" />

    <!-- Modal used to generate new backup codes -->
    <generate-two-factor-backup-codes v-model="showGenerateBackupCodes" />

    <v-main :class="[`d-flex align-stretch`, options.mainContentClass]">
      <slot name="content"></slot>
    </v-main>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { EventBus } from '@/event-bus.js';
import AuthIdle from '@/modules/shared/components/AuthIdle.vue';
import AccountSettingsDialog from '@/modules/shared/components/AccountSettings.vue';
import AccessControlMixin from '@/mixins/accessControl.mixin';
import TwoFactorBanner from '@/modules/shared/components/TwoFactorBanner.vue';
import EnableDisableTwoFactor from '../../EnableDisableTwoFactor.vue';
import GenerateTwoFactorBackupCodes from '../../GenerateTwoFactorBackupCodes.vue';
import ActionItemsIntro from '@/modules/shared/components/action-items/ActionItemsIntro.vue';
import { ActionItemsService } from '@/modules/shared/services/action-items.service';

const defaultOptions = {
  showSystemBar: false,
  showSearchBar: false,
  showUserOptions: true,
  showMenuToggle: true,

  mainContentClass: '',
  appBarColor: '#fafbfc',
  miniDrawerColor: '',
  miniDrawerIconsColor: 'white',
  leftDrawerColor: 'white',
  leftDrawerIconsColor: ''
};

export default {
  name: 'ApplicationLayout',
  components: {
    AuthIdle,
    AccountSettingsDialog,
    ActionItemsIntro,
    TwoFactorBanner,
    EnableDisableTwoFactor,
    GenerateTwoFactorBackupCodes
  },
  mixins: [AccessControlMixin],

  props: {
    options: {
      type: Object,
      default: () => defaultOptions
    },

    version: {
      type: String,
      default: () => '1.0'
    },
    user: {
      type: Object,
      default: () => {}
    },
    links: {
      type: Array,
      default: () => []
    },
    showActionItems: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    unWatchOptions: null,
    mini: true,
    drawer: null,
    showRightDrawer: false,
    showEnableDisable2FA: false,
    showGenerateBackupCodes: false,
    actionItems: {}
  }),

  created() {
    //Watch for events
    EventBus.$on('SHOW_RIGHT_DRAWER', (event) => {
      this.showRightDrawer = event;
    });

    if (this.showActionItems) {
    this.fetchActionItems();
    }

    this.unWatchOptions = this.$watch(
      'options',
      (cur) => {
        for (const k in defaultOptions) {
          if (!cur[k] && typeof cur[k] !== 'boolean')
            cur[k] = defaultOptions[k];
        }

        //unwatch options
        if (this.unWatchOptions) this.unWatchOptions();
      },
      { immediate: true }
    );
  },

  computed: {
    ...mapGetters('roleModule', ['roles']),
    ...mapGetters('authModule', ['getUserAvatar']),

    userFullName() {
      // check if first and last name is on user object and return the fullname
      if (this.user?.firstName && this.user?.lastName)
        return `${this.user.firstName} ${this.user.lastName}`;
      // check if first and last name is on person object and return the fullname
      if (this.user?.person?.firstName && this.user?.person?.lastName)
        return `${this.user?.person?.firstName} ${this.user?.person?.lastName}`;
      // check if username is present on user object
      if (this.user?.username) return this.user?.username;
      // fallback to Super Admin
      return 'Super Admin';
    },
    // get the user role if the user & roles are defined
    userRole() {
      if (!this.user) return null;
      if (!this.roles) return null;
      return this.roles.find((r) => r.id == this.user?.role)?.name;
    }
  },

  methods: {
    ...mapActions('authModule', ['signOut']),

    itemHasChildren(item) {
      if (item && item.items && item.items.some((i) => i.items)) return true;
    },

    toggleRightDrawer() {
      EventBus.$emit('TOGGLE_RIGHT_DRAWER', true);
    },

    async logOut() {
      await this.signOut(this.user.id);
      this.$router.replace('/auth');
    },

    async fetchActionItems() {
      try {
        const svc = new ActionItemsService();
        const response = await svc.getActionItemsForLoggedInUser();
        this.actionItems = response.data;
      } catch (error) {
        console.error('Failed to load action items:', error);
      }
    }
  }
};
</script>

<style lang="sass" scoped>
  .v-menu__content
    border: 1px solid #ddd

.small-list-item
  max-height: 36px !important
  height: 36px !important
  font-size: 11px

.border
  border: 1px solid #ddd
</style>
